import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TablePagination,
} from "@mui/material";
import axiosInstance from "../axiosInstance";
import { useParams } from "react-router-dom";

const apiUrl = process.env.REACT_APP_API_URL;

const SFTPLogViewPage = () => {
  const [log, setLog] = useState(null);
  const [searchVPC, setSearchVPC] = useState("");
  const [matchFilter, setMatchFilter] = useState("all");
  const [filteredOutput, setFilteredOutput] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { id } = useParams();

  useEffect(() => {
    const fetchLog = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          console.error("Unauthorized. Please login again.");
          return;
        }

        const response = await axiosInstance.get(`${apiUrl}/sftp-log-detail/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setLog(response.data);
        setFilteredOutput(response.data.output || []); // Initialize filtered output
      } catch (error) {
        console.error("Error fetching log:", error);
      }
    };
    fetchLog();
  }, [id]);

  // Apply search & filter whenever searchVPC or matchFilter changes
  useEffect(() => {
    if (log?.output) {
      const updatedOutput = log.output.filter((item) => {
        const matchesVPC = searchVPC ? item.vpc.toLowerCase().includes(searchVPC.toLowerCase()) : true;
        const matchesStatus =
          matchFilter === "all" ? true : matchFilter === "matched" ? item.matched === 1 : item.matched === 0;
        return matchesVPC && matchesStatus;
      });

      setFilteredOutput(updatedOutput);
      setPage(0); // Reset to first page on filter change
    }
  }, [searchVPC, matchFilter, log]);

  if (!log) return <Typography variant="h6">Loading...</Typography>;

  // Paginate AFTER filtering
  const paginatedOutput = filteredOutput.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Handle pagination
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h4" gutterBottom>
        SFTP Log Details
      </Typography>

      {/* Log Summary Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Log Details
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography>
              <strong>Vendor:</strong> {log.vendorName ? `${log.vendorName} (ID: ${log.vendorId})` : log.vendorId}
            </Typography>
            <Typography>
              <strong>Cron Expression:</strong> {log.cronExpression} | {log.scheduleType}
            </Typography>
            <Typography>
              <strong>Host:</strong> {log.host}
             
            </Typography>
            <Typography>
              <strong>Ran At:</strong> {log.createdAt}
            </Typography>
            <Typography>
              <strong>Matches:</strong> {log.matches} | <strong>Unmatched:</strong> {log.unmatched} |{" "}
              <strong>Count:</strong> {log.count}
            </Typography>
            <Typography>
              <strong>Status:</strong>{" "}
              <Chip
                label={log.status === 200 ? "Success" : log.status === 206 ? "Partial Success" : "Failed"}
                color={log.status === 200 ? "success" : log.status === 206 ? "warning" : "error"}
              />
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Search & Filter Controls */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          label="Search VPC"
          variant="outlined"
          size="small"
          value={searchVPC}
          onChange={(e) => setSearchVPC(e.target.value)}
          fullWidth
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Match Status</InputLabel>
          <Select value={matchFilter} onChange={(e) => setMatchFilter(e.target.value)} label="Match Status">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="matched">Matched</MenuItem>
            <MenuItem value="unmatched">Unmatched</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Output Table */}
      <Typography variant="h6" gutterBottom>
        Output Details
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>VPC</strong></TableCell>
              <TableCell><strong>Qty Sent</strong></TableCell>
              <TableCell><strong>Cost Sent</strong></TableCell>
              <TableCell><strong>Vendor Name</strong></TableCell>
              <TableCell><strong>Product ID</strong></TableCell>
              <TableCell><strong>SKU ID</strong></TableCell>
              <TableCell><strong>RO Qty</strong></TableCell>
              <TableCell><strong>Match Status</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedOutput.length > 0 ? (
              paginatedOutput.map((item, i) => (
                <TableRow key={i}>
                  <TableCell>{item.vpc}</TableCell>
                  <TableCell>{item.quantity_sent ?? "N/A"}</TableCell>
                  <TableCell>{item.unit_cost_sent != null ? item.unit_cost_sent.toFixed(2) : "N/A"}</TableCell>
                  <TableCell>{item.vendor_name || "N/A"}</TableCell>
                  <TableCell>{item.id || "N/A"}</TableCell>
                  <TableCell>{item.sku_id || "N/A"}</TableCell>
                  <TableCell>{item.quantity ?? "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.matched === 0 ? "Not Matched" : "Matched"}
                      color={item.matched === 0 ? "error" : "success"}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No results found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredOutput.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default SFTPLogViewPage;
